import { Button } from 'primereact/button';
import { useEffect, useRef, useState } from 'react';

export const CamaraComponent = ({ onSuccessPhoto, initCamera, mostrarBotonArchivos = true }) => {
    const [streamActive, setStreamActive] = useState(false);
    const [camaraError, setCamaraError] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        if (initCamera) {
            setCamaraError(null);
            iniciarCamara();
        }
        return () => detenerCamara();
    }, [initCamera]);

    /* ========================
       🎥 CÁMARA
    ======================== */
    const iniciarCamara = async () => {
        try {
            detenerCamara();
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } }
            });
            streamRef.current = stream;
            setCamaraError(null);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;

                // Esperar a que el stream realmente cargue
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                    setStreamActive(true); // 🔥 Aquí sí se actualiza correctamente
                };
            }
        } catch (err) {
            setCamaraError('No se pudo acceder a la cámara. Verifique permisos.');
            setStreamActive(false);
        }
    };

    const detenerCamara = () => {
        try {
            // 1. Detener tracks del stream
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => {
                    track.stop();
                });
            }

            // 2. Pausar y limpiar el video
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.srcObject = null;
                videoRef.current.onloadedmetadata = null; // limpiar callback
            }

            // 3. Limpiar referencia
            streamRef.current = null;

            // 4. Actualizar estado
            setStreamActive(false);
        } catch (e) {
            console.warn('Error al detener cámara:', e);
        }
    };

    /* ========================
           📸 TOMAR FOTO
        ======================== */
    const tomarFoto = () => {
        if (!videoRef.current || !canvasRef.current || !streamActive) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);

        canvas.toBlob(
            (blob) => {
                const photoFile = new File([blob], `trazabilidad_${Date.now()}.jpg`, { type: 'image/jpeg' });
                onSuccessPhoto(photoFile);
            },
            'image/jpeg',
            0.95
        );
    };

    /* ========================
       📁 SELECCIONAR DE ARCHIVOS
    ======================== */
    const fileInputRef = useRef(null);

    const seleccionarDeArchivos = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = null; // Limpiar selección previa
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Convertir a JPEG vía canvas para garantizar compatibilidad con Java ImageIO en el backend
        const img = new window.Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const jpegFile = new File(
                            [blob],
                            `evidencia_${Date.now()}.jpg`,
                            { type: 'image/jpeg' }
                        );
                        onSuccessPhoto(jpegFile);
                    }
                },
                'image/jpeg',
                0.90
            );
            URL.revokeObjectURL(img.src);
        };
        img.onerror = () => {
            URL.revokeObjectURL(img.src);
            // Si el navegador no puede decodificar (HEIC en Android), enviar el original.
            // El backend se encarga de convertir a JPEG antes de subir a OneDrive.
            console.warn('Navegador no pudo decodificar imagen, enviando original al backend para conversión:', file.name);
            onSuccessPhoto(file);
        };
        img.src = URL.createObjectURL(file);
    };
    
    return (
        <div>
            {/* Cámara */}
            <video ref={videoRef} autoPlay muted playsInline className="w-full border-round" style={{ maxHeight: 320, objectFit: 'cover', border: '1px solid #ddd' }} />
            <canvas ref={canvasRef} className="hidden" />

            {camaraError && <small className="text-red-600">⚠️ {camaraError}</small>}
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
            <Button label="Tomar Foto" icon="pi pi-camera" className="w-full" onClick={tomarFoto} disabled={!streamActive} />
            { mostrarBotonArchivos && 
            (<Button label="Seleccionar de Archivos" icon="pi pi-folder-open" className="w-full p-button-secondary" onClick={seleccionarDeArchivos} />)
            }
        </div>
    );
};
